/* eslint-disable @typescript-eslint/no-empty-function */
import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useContext,
  useState,
} from "react";

import { useRootFontSize } from "hooks/useRootFontSize";
import { useScrollLock } from "utils/lockBodyScroll";

export enum ThemeMode {
  LIGHT = "light",
  DARK = "dark",
}

export enum HeaderVariant {
  STICKY = "sticky",
  SEMI_STICKY = "semi-sticky",
}

export enum ScrollLockItem {
  SEARCH = "search",
  MENU = "menu",
}

export type contextProps = {
  themeMode: ThemeMode;
  headerVariant: HeaderVariant;
  pageHeader: ReactElement | null;
  pageStickyHeader: ReactElement | null;
  disableDocumentThemeMode: boolean;
  blur: boolean;
  setThemeMode: (theme: ThemeMode) => void;
  setPageHeader: (e: ReactElement | null) => void;
  setPageStickyHeader: (e: ReactElement | null) => void;
  setHeaderVariant: (variant: HeaderVariant) => void;
  setDisableDocumentThemeMode: (bool: boolean) => void;
  setBlur: (bool: boolean) => void;
  addScrollLock: (e: ScrollLockItem) => void;
  removeScrollLock: (e: ScrollLockItem) => void;
  rootFontSize: number;
};

const LayoutContext = createContext<contextProps>({
  themeMode: ThemeMode.DARK,
  headerVariant: HeaderVariant.STICKY,
  pageHeader: null,
  pageStickyHeader: null,
  disableDocumentThemeMode: false,
  rootFontSize: 16,
  blur: false,
  setThemeMode: () => {},
  setPageHeader: () => {},
  setPageStickyHeader: () => {},
  setHeaderVariant: () => {},
  setDisableDocumentThemeMode: () => {},
  addScrollLock: () => {},
  removeScrollLock: () => {},
  setBlur: () => {},
});

export const LayoutContextProvider = ({ children }: PropsWithChildren) => {
  const [pageHeader, setPageHeader] = useState<ReactElement | null>(null);
  const [themeMode, setThemeMode] = useState<ThemeMode>(ThemeMode.DARK);
  const [headerVariant, setHeaderVariant] = useState(HeaderVariant.STICKY);
  const [pageStickyHeader, setPageStickyHeader] = useState<ReactElement | null>(
    null
  );
  const [disableDocumentThemeMode, setDisableDocumentThemeMode] =
    useState(false);
  const [blur, setBlur] = useState(false);
  const [scrollLocks, setScrollLocks] = useState<Array<ScrollLockItem>>([]);
  const rootFontSize = useRootFontSize();
  const { lockBodyScroll, unlockBodyScroll } = useScrollLock();

  function addScrollLock(lock: ScrollLockItem) {
    if (!scrollLocks.includes(lock)) {
      setScrollLocks([...scrollLocks, lock]);
    }

    if (scrollLocks.length >= 0) lockBodyScroll();
  }

  function removeScrollLock(lock: ScrollLockItem) {
    const lockExists = scrollLocks.indexOf(lock) !== -1;

    if (lockExists) {
      const newScrollLocks = scrollLocks.filter((item) => item !== lock);

      setScrollLocks(newScrollLocks);
    }

    if (!scrollLocks.length) unlockBodyScroll();
  }

  return (
    <LayoutContext.Provider
      value={{
        themeMode,
        pageHeader,
        pageStickyHeader,
        headerVariant,
        disableDocumentThemeMode,
        rootFontSize,
        blur,
        setThemeMode,
        setPageHeader,
        setPageStickyHeader,
        setHeaderVariant,
        setDisableDocumentThemeMode,
        addScrollLock,
        removeScrollLock,
        setBlur,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayoutContext = () => useContext(LayoutContext);
